/**
 * MyRoleChip — small badge showing the current user's effective role on
 * a client/brand row (as returned by the backend `my_role` field). Adds
 * a tooltip listing the concrete permissions the role grants.
 *
 * Renders nothing when the user has no explicit membership (my_role is
 * null) — the client/brand list already filters those out for
 * non-admins, so this stays out of the way for regular users.
 */
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const TONE = {
  owner:   "border-emerald-500/70 text-emerald-700 dark:text-emerald-300 bg-emerald-500/10",
  manager: "border-sky-500/70 text-sky-700 dark:text-sky-300 bg-sky-500/10",
  member:  "border-slate-500/70 text-slate-700 dark:text-slate-300 bg-slate-500/10",
  viewer:  "border-neutral-400/70 text-neutral-600 dark:text-neutral-300 bg-neutral-500/10",
};

export default function MyRoleChip({ role, perms, entityKind = "client", size = "xs" }) {
  if (!role) return null;
  const cls = TONE[role.toLowerCase()] || TONE.viewer;
  const px = size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-1.5 py-[1px] text-[10px]";
  const label = role.charAt(0).toUpperCase() + role.slice(1);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 border ${cls} ${px} tabular-nums cursor-help`}
          aria-label={`Your role on this ${entityKind}: ${role}`}
          data-testid={`my-role-chip-${role}`}
        >
          <span className="opacity-60">you:</span> {label}
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" className="w-64 text-xs p-2">
        <div className="font-semibold mb-1">You are a {label} on this {entityKind}</div>
        {perms?.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {perms.map((p) => (
              <span key={p} className="text-[10px] px-1 border border-border">{p}</span>
            ))}
          </div>
        ) : (
          <div className="text-muted-foreground">No enumerated permissions.</div>
        )}
      </PopoverContent>
    </Popover>
  );
}
